import { Controller } from "@hotwired/stimulus";

type WidthBounds = {
    min: number;
    max: number;
};

const STORAGE_KEY = "globalAssistant.sessionsPaneWidth";
const WIDTH_PROPERTY = "--global-assistant-sessions-width";
const STORED_WIDTH_PROPERTY = "--global-assistant-sessions-stored-width";
const DEFAULT_WIDTH = 288;
const KEYBOARD_STEP = 32;

export default class extends Controller<HTMLElement> {
    static targets = ["conversation", "divider", "sessions"];

    declare readonly conversationTarget: HTMLElement;
    declare readonly dividerTarget: HTMLElement;
    declare readonly sessionsTarget: HTMLElement;

    private dragging = false;
    private resizeObserver: ResizeObserver | null = null;

    connect(): void {
        this.move = this.move.bind(this);
        this.stop = this.stop.bind(this);
        this.reconcile = this.reconcile.bind(this);

        const storedWidth = this.storedWidth();
        this.applyWidth(storedWidth ?? DEFAULT_WIDTH, false);
        this.resizeObserver = new ResizeObserver(this.reconcile);
        this.resizeObserver.observe(this.element);
    }

    disconnect(): void {
        this.stop();
        this.resizeObserver?.disconnect();
    }

    start(event: PointerEvent): void {
        if (event.button !== 0) return;

        event.preventDefault();
        this.dragging = true;
        this.dividerTarget.setPointerCapture?.(event.pointerId);
        document.documentElement.classList.add("select-none");
        document.documentElement.style.cursor = "col-resize";
        window.addEventListener("pointermove", this.move);
        window.addEventListener("pointerup", this.stop, { once: true });
    }

    keydown(event: KeyboardEvent): void {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

        event.preventDefault();
        const delta = event.key === "ArrowRight" ? KEYBOARD_STEP : -KEYBOARD_STEP;
        this.applyWidth(this.sessionsTarget.getBoundingClientRect().width + delta);
    }

    private move(event: PointerEvent): void {
        if (!this.dragging) return;

        const shell = this.element.getBoundingClientRect();
        const width = event.clientX - shell.left - this.dividerTarget.offsetWidth / 2;
        this.applyWidth(width);
    }

    private stop(): void {
        if (!this.dragging) return;

        this.dragging = false;
        document.documentElement.classList.remove("select-none");
        document.documentElement.style.cursor = "";
        window.removeEventListener("pointermove", this.move);
        window.removeEventListener("pointerup", this.stop);
    }

    private reconcile(): void {
        this.applyWidth(this.sessionsTarget.getBoundingClientRect().width || DEFAULT_WIDTH, false);
    }

    private applyWidth(rawWidth: number, persist = true): void {
        const bounds = this.widthBounds();
        const width = Math.min(Math.max(rawWidth, bounds.min), bounds.max);

        this.element.style.setProperty(WIDTH_PROPERTY, `${width}px`);
        if (persist || this.storedWidth() !== null) {
            document.documentElement.style.setProperty(STORED_WIDTH_PROPERTY, `${width}px`);
        }
        this.dividerTarget.setAttribute("aria-valuemin", Math.round(bounds.min).toString());
        this.dividerTarget.setAttribute("aria-valuemax", Math.round(bounds.max).toString());
        this.dividerTarget.setAttribute("aria-valuenow", Math.round(width).toString());

        if (persist) window.localStorage.setItem(STORAGE_KEY, Math.round(width).toString());
    }

    private widthBounds(): WidthBounds {
        const shellWidth = this.element.getBoundingClientRect().width;
        const dividerWidth = this.dividerTarget.offsetWidth || 0;
        const min = Math.max(224, Math.min(320, shellWidth * 0.18));
        const minConversationWidth = Math.max(520, Math.min(760, shellWidth * 0.55));
        const max = Math.max(min, shellWidth - minConversationWidth - dividerWidth);
        return { min, max };
    }

    private storedWidth(): number | null {
        const value = Number.parseInt(window.localStorage.getItem(STORAGE_KEY) || "", 10);
        return Number.isFinite(value) ? value : null;
    }
}
